import { useState } from "react";
import styled from "@emotion/styled";
import { Text } from "styles/utility-components";

import { generateSavedViewUrl } from "components/saved_searches/utils";
import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { usePinView } from "lib/hooks/api/notices/views/usePinView";
import { useUnpinView } from "lib/hooks/api/notices/views/useUnpinView";
import { SavedViewDataTypeUnion } from "lib/types/savedViews";
import { BasicView } from "../SavedViewsMenu";
import ViewAllButton from "../ViewAllButton";

type TemplatesProps = {
  dataType: SavedViewDataTypeUnion;
  templates?: BasicView[];
  pinnedViews: BasicView[];
  setSelectedView: (viewId?: string, viewName?: string, context?: string) => void;
};

function Templates({ dataType, templates, pinnedViews, setSelectedView }: TemplatesProps) {
  const { mutate: unpinView, isLoading: isLoadingUnpin } = useUnpinView();
  const { mutate: pinView, isLoading: isLoadingPin } = usePinView();
  const [pinningId, setPinningId] = useState<string | null>(null);
  const [unpinningId, setUnpinningId] = useState<string | null>(null);

  const isPinned = (id: string) => pinnedViews?.some((view) => view.id === id);

  const handlePin = (id: string, name: string) => {
    setPinningId(id);

    pinView(
      { id, name },
      {
        onSuccess: () => setPinningId(null),
        onError: () => setPinningId(null),
      },
    );
  };

  const handleUnpin = (id: string, name: string) => {
    setUnpinningId(id);

    unpinView(
      { id, name },
      { onSuccess: () => setUnpinningId(null), onError: () => setUnpinningId(null) },
    );
  };

  return (
    <TemplatesWrapper>
      <TemplatesListScroll>
        {templates?.map((view) => {
          const { id, name } = view;

          return (
            <TemplatesItem key={id}>
              <Button
                as="a"
                buttonType="tertiary"
                onClick={(event) => {
                  event.preventDefault();
                  setSelectedView(id, name, "Templates list");
                }}
                rel="noopener noreferrer"
                blackText
                justifyContent="space-between"
                fullWidth
                href={generateSavedViewUrl(view.id, view.dataType)}
              >
                <Text fontWeight="500">{name}</Text>

                <span className="pinActionIcon">
                  {isPinned(id) ? (
                    <Tooltip>
                      <Button
                        buttonType="tertiary"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleUnpin(id, name);
                        }}
                        icon="pin02"
                        iconProps={{ fill: "currentColor" }}
                        loading={isLoadingUnpin && unpinningId === id}
                        aria-label="Unpin this view"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <Button
                        buttonType="tertiary"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handlePin(id, name);
                        }}
                        icon="pin02"
                        loading={isLoadingPin && pinningId === id}
                        aria-label="Pin this view"
                      />
                    </Tooltip>
                  )}
                </span>
              </Button>
            </TemplatesItem>
          );
        })}
      </TemplatesListScroll>
      <ViewAllButton filters={{ dataTypes: [dataType] }} fullWidth />
    </TemplatesWrapper>
  );
}

const TemplatesWrapper = styled.div({
  display: "flex",
  backgroundColor: "white",
  flexDirection: "column",
  justifyContent: "space-between",
  height: "100%",
  paddingTop: 8,
  paddingLeft: 8,
});

const TemplatesListScroll = styled.div({
  flex: 1,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const TemplatesItem = styled.div({
  display: "flex",
  alignItems: "center",
  button: {
    padding: "0 8px",
  },

  ":hover": {
    color: "#0052cc",
    cursor: "pointer",

    ".pinActionIcon button": {
      display: "inline-block",
      backgroundColor: "transparent",
    },
  },
  ".pinActionIcon": {
    borderRadius: 4,

    ":hover": {
      backgroundColor: "#1890ff26",
    },
  },
});

export default Templates;
